// src/features/tox/pages/TalkToxDetailPage.tsx
import React, { useEffect, useState, useRef, useLayoutEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/ui/PageShell";
import { withBase, HEADER } from "@/ui/Header";
import { usePageScale } from "@/ui/usePageScale";
import { notify } from "@/ui/notify";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";
import {
    api,
    WeeklyTox,
    weeklyToxGet,
    weeklyToxReact,
    ToxReactionType,
    isLoggedIn,
} from "@/lib/api";
import { customConfirm } from "@/ui/confirm";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";

/* ───────────── Layout spec ───────────── */
const BASE = {
    W: 1264,
    INNER_W: 1060,
    MARGIN_X: 102,
    CONTENT_TOP_GAP: 40,
    PAGER_NAV_H: 80,
} as const;

/* ───────────── Icons ───────────── */
const ICONS = {
    back: withBase("icons/prev1.svg"),
    first: withBase("icons/prev2.svg"),
    prev: withBase("icons/prev1.svg"),
    next: withBase("icons/next1.svg"),
    last: withBase("icons/next2.svg"),

    like: withBase("icons/reaction_like.svg"),
    funny: withBase("icons/reaction_funny.svg"),
    wow: withBase("icons/reaction_wow.svg"),
    angry: withBase("icons/reaction_angry.svg"),
    sad: withBase("icons/reaction_sad.svg"),
} as const;

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

/* ───────────── Util ───────────── */
function resolveImageUrl(raw?: string | null): string | undefined {
    if (!raw) return undefined;
    if (raw.startsWith("/") && API_BASE) return `${API_BASE}${raw}`;
    if (raw.startsWith("http://localhost") || raw.startsWith("https://localhost")) {
        if (!API_BASE) return raw;
        try {
            const u = new URL(raw);
            const base = new URL(API_BASE);
            u.protocol = base.protocol;
            u.hostname = base.hostname;
            u.port = base.port;
            return u.toString();
        } catch {
            return raw;
        }
    }
    return raw;
}

type LocationState = {
    fromPage?: number;
    fromTab?: string;
};

/** 백엔드와 맞춰 쓸 리액션 종류 매핑 */
const REACTIONS = [
    { key: "LIKE" as ToxReactionType, code: "like", label: "좋아요" },
    { key: "FUNNY" as ToxReactionType, code: "funny", label: "웃겨요" },
    { key: "WOW" as ToxReactionType, code: "wow", label: "놀라워요" },
    { key: "ANGRY" as ToxReactionType, code: "angry", label: "화나요" },
    { key: "SAD" as ToxReactionType, code: "sad", label: "슬퍼요" },
] as const;

type ReactionCounts = {
    like: number;
    funny: number;
    wow: number;
    angry: number;
    sad: number;
    total: number;
};

/* ───────────── 페이지 타입 ───────────── */
type PageItem =
    | { type: 'image'; content: string }
    | { type: 'text'; content: string; isHtml?: boolean; showReaction?: boolean }
    | { type: 'reaction' };

/* ───────────── 콘텐츠 페이저 ───────────── */
/* ───────────── 콘텐츠 페이저 ───────────── */
type ContentPagerHandle = { goTo: (n: number) => void; getTotal: () => number };
type ContentPagerProps = {
    imageUrl?: string;
    text: string;
    height: number;
    fontPx?: number;
    linePx?: number;
    padY?: number;
    padX?: number;
    onMeta?: (pageIdx: number, total: number) => void;
    s: (n: number) => number;
    px: (n: number) => string;
    sf: (n: number) => number;

    // 공감하기용 props
    reactions: ReactionCounts;
    userReaction: ToxReactionType | null;
    onReact: (t: ToxReactionType) => void;

    // ✅ 추가: 버튼 네비게이션 즉시 전환
    instantNav?: boolean;

    // ✅ 추가: 프리뷰 길이 (초기 즉시 렌더용)
    previewChars?: number;
};

/** HTML 태그 제거 후 plain text 반환 (preview용) */
function stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
}

/** ✅ measurer는 전역 1개만 재사용 (WebView 성능) */
let __pagerMeasurer: HTMLDivElement | null = null;
function getPagerMeasurer() {
    if (typeof document === "undefined") return null;
    if (__pagerMeasurer) return __pagerMeasurer;

    const m = document.createElement("div");
    m.style.position = "absolute";
    m.style.visibility = "hidden";
    m.style.pointerEvents = "none";
    m.style.left = "-99999px";
    m.style.top = "-99999px";
    m.style.whiteSpace = "pre-wrap";
    m.style.wordBreak = "break-word";
    document.body.appendChild(m);
    __pagerMeasurer = m;
    return m;
}

/** ✅ UI 프리징 방지용 yield */
const yieldToUI = () =>
    new Promise<void>((r) => requestAnimationFrame(() => r()));

const ContentPager = forwardRef<ContentPagerHandle, ContentPagerProps>(function ContentPager(
    {
        imageUrl,
        text,
        height,
        fontPx = 32,
        linePx = 52,
        padY = 20,
        padX = 0,
        onMeta,
        s,
        px,
        sf,
        reactions,
        userReaction,
        onReact,
        instantNav = true,
        previewChars = 700, // ✅ 기본 프리뷰 길이
    },
    ref
) {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [pages, setPages] = useState<PageItem[]>([]);
    const [page, setPage] = useState(0);

    // ✅ 분할 계산 진행 상태 (로딩 표시용)
    const [splitting, setSplitting] = useState(false);

    const onMetaRef = useRef(onMeta);
    useEffect(() => {
        onMetaRef.current = onMeta;
    }, [onMeta]);

    const fitChars = (str: string, maxH: number, measurer: HTMLDivElement) => {
        let lo = 1,
            hi = Math.max(1, str.length),
            fit = 0;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            measurer.textContent = str.slice(0, mid);
            if (measurer.scrollHeight <= maxH) {
                fit = mid;
                lo = mid + 1;
            } else hi = mid - 1;
        }
        return fit;
    };

    /** 텍스트를 visual line 단위로 분할 후 linesPerPage씩 묶어 페이지 배열 반환 */
    const computeTextPagesAsync = async (availH: number, availW: number, rawText: string, lastPageReserveH = 0) => {
        const measurer = getPagerMeasurer();
        if (!measurer) return [""];

        measurer.style.width = `${availW}px`;
        measurer.style.fontSize = `${fontPx}px`;
        measurer.style.lineHeight = `${linePx}px`;
        measurer.style.whiteSpace = "pre-wrap";
        measurer.style.wordBreak = "break-word";

        const linesPerPage = Math.max(1, Math.floor(availH / linePx)+10);
        const text = rawText || "내용이 없습니다.";
        const rawLines = text.split("\n");
        const visualLines: string[] = [];

        for (let li = 0; li < rawLines.length; li++) {
            let remaining = rawLines[li];
            if (!remaining) {
                visualLines.push("");
                continue;
            }
            while (remaining.length > 0) {
                measurer.textContent = remaining;
                if (measurer.scrollHeight <= linePx + 2) {
                    visualLines.push(remaining);
                    break;
                }
                // 한 줄에 들어가는 글자 수 이진탐색
                const fit = fitChars(remaining, linePx + 2, measurer);
                visualLines.push(remaining.slice(0, Math.max(1, fit)));
                remaining = remaining.slice(Math.max(1, fit));
            }
            if (li % 10 === 0) await yieldToUI();
        }

        // linesPerPage 줄씩 묶기 (1차)
        const pages: string[] = [];
        for (let i = 0; i < visualLines.length; i += linesPerPage) {
            pages.push(visualLines.slice(i, i + linesPerPage).join("\n"));
        }

        // 마지막 페이지에 리액션 공간 확보: 넘치는 줄은 새 페이지로 분리 (2차)
        if (lastPageReserveH > 0 && pages.length > 0) {
            const linesPerPageLast = Math.max(1, Math.floor((availH - lastPageReserveH) / linePx));
            if (linesPerPageLast < linesPerPage) {
                const lastLines = (pages[pages.length - 1] || "").split("\n");
                if (lastLines.length > linesPerPageLast) {
                    pages[pages.length - 1] = lastLines.slice(0, linesPerPageLast).join("\n");
                    pages.push(lastLines.slice(linesPerPageLast).join("\n"));
                }
            }
        }

        return pages.length ? pages : [""];
    };

    /** HTML 콘텐츠를 block 단위로 잘라 페이지 배열 반환 */
    const computeHtmlPagesAsync = async (availH: number, availW: number, html: string, lastPageReserveH = 0): Promise<string[]> => {
        const measurer = getPagerMeasurer();
        if (!measurer) return ["<p>내용이 없습니다.</p>"];

        measurer.style.width = `${availW}px`;
        measurer.style.fontSize = `${fontPx}px`;
        measurer.style.lineHeight = `${linePx}px`;
        measurer.style.whiteSpace = "normal";
        measurer.style.wordBreak = "break-word";

        // HTML을 block 단위로 분리
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const blocks: string[] = [];
        tempDiv.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                const t = (node.textContent || '').trim();
                if (t) blocks.push(`<span>${t}</span>`);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                blocks.push((node as Element).outerHTML);
            }
        });
        if (!blocks.length) return ["<p>내용이 없습니다.</p>"];

        const pages: string[] = [];
        let currentHtml = '';
        let i = 0;

        while (i < blocks.length) {
            const block = blocks[i];
            const testHtml = currentHtml + block;
            measurer.innerHTML = testHtml;

            if (measurer.scrollHeight <= availH) {
                currentHtml = testHtml;
                i++;
            } else {
                if (currentHtml) {
                    pages.push(currentHtml);
                    currentHtml = '';
                    // i 유지: 같은 block을 빈 페이지에서 재시도
                } else {
                    // 이 block 하나만으로도 높이 초과 → 그냥 push
                    pages.push(block);
                    currentHtml = '';
                    i++;
                }
            }

            if (i % 5 === 0) await yieldToUI();
        }

        if (currentHtml) pages.push(currentHtml);

        // 마지막 페이지에 리액션 공간 확보 (HTML 모드)
        if (lastPageReserveH > 0 && pages.length > 0) {
            const lastPageMaxH = availH - lastPageReserveH;
            measurer.innerHTML = pages[pages.length - 1];
            if (measurer.scrollHeight > lastPageMaxH) {
                const lastDiv = document.createElement('div');
                lastDiv.innerHTML = pages[pages.length - 1];
                const lastBlocks: string[] = [];
                lastDiv.childNodes.forEach(node => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        const t = (node.textContent || '').trim();
                        if (t) lastBlocks.push(`<span>${t}</span>`);
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                        lastBlocks.push((node as Element).outerHTML);
                    }
                });
                let fitsHtml = '';
                let overflowStart = lastBlocks.length;
                for (let j = 0; j < lastBlocks.length; j++) {
                    const test = fitsHtml + lastBlocks[j];
                    measurer.innerHTML = test;
                    if (measurer.scrollHeight <= lastPageMaxH) {
                        fitsHtml = test;
                    } else {
                        overflowStart = j;
                        break;
                    }
                }
                const overflowHtml = lastBlocks.slice(overflowStart).join('');
                if (overflowHtml) {
                    pages[pages.length - 1] = fitsHtml || lastBlocks[0] || '';
                    pages.push(overflowHtml);
                }
            }
        }

        return pages.length ? pages : ["<p>내용이 없습니다.</p>"];
    };

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        let cancelled = false;

        // ✅ 1) 프리뷰 페이지 먼저 즉시 렌더
        const rawText = (text || "").trim().replace(/\n{2,}/g, '\n');
        const htmlMode = rawText.startsWith('<');
        const safeText = rawText || (htmlMode ? "<p>내용이 없습니다.</p>" : "내용이 없습니다.");

        // 프리뷰: HTML이면 태그 제거 후 plain text로 표시
        const previewPlain = htmlMode ? stripHtml(safeText) : safeText;
        const preview = previewPlain.length > previewChars
            ? previewPlain.slice(0, previewChars).trimEnd() + "…"
            : previewPlain;

        const initialPages: PageItem[] = [];
        if (imageUrl) initialPages.push({ type: "image", content: imageUrl });
        initialPages.push({ type: "text", content: preview, isHtml: false, showReaction: true });

        setPages(initialPages);
        setPage(0);
        onMetaRef.current?.(0, initialPages.length);

        // ✅ 2) 실제 페이지 분할은 비동기로 진행
        (async () => {
            setSplitting(true);

            await yieldToUI();

            if (cancelled) return;

            const availW = Math.max(0, el.clientWidth - padX * 2 - 4);
            const availH = Math.max(0, (height - padY * 2)+100 );

            // 리액션 섹션 높이 추정: marginTop + 상하 padding + 제목 + 버튼 행
            const reactionSectionH =
                s(16) +                        // marginTop
                s(32) * 2 +                    // padding top + bottom
                Math.ceil(s(36) * 1.3) + s(24) + // 제목 높이 + marginBottom
                s(100) + s(8) + Math.ceil(s(28) * 1.4); // 아이콘 + gap + 카운트

            const allPages: PageItem[] = [];

            if (imageUrl) allPages.push({ type: "image", content: imageUrl });

            if (htmlMode) {
                const htmlPages = await computeHtmlPagesAsync(availH, availW, safeText, reactionSectionH);
                if (cancelled) return;
                htmlPages.forEach((p, i) => allPages.push({ type: "text", content: p, isHtml: true, showReaction: i === htmlPages.length - 1 }));
            } else {
                const textPages = await computeTextPagesAsync(availH, availW, safeText, reactionSectionH);
                if (cancelled) return;
                textPages.forEach((p, i) => allPages.push({ type: "text", content: p, showReaction: i === textPages.length - 1 }));
            }

            setPages(allPages);
            setPage(0);

            // scroll 위치 초기화
            const list = listRef.current;
            if (list) {
                try {
                    (list as any).scrollTo({ left: 0, behavior: "auto" });
                } catch {
                    list.scrollLeft = 0;
                }
            }

            onMetaRef.current?.(0, allPages.length);
            setSplitting(false);
        })();

        return () => {
            cancelled = true;
            setSplitting(false);
        };
    }, [imageUrl, text, height, fontPx, linePx, padY, padX, previewChars]);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        const onScroll = () => {
            const idx = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
            if (idx !== page) {
                setPage(idx);
                onMetaRef.current?.(idx, Math.max(1, pages.length || 1));
            }
        };

        el.addEventListener("scroll", onScroll as any, { passive: true } as any);
        return () => el.removeEventListener("scroll", onScroll as any);
    }, [page, pages.length]);

    const go = (to: number) => {
        const el = listRef.current;
        if (!el) return;

        const totalPages = Math.max(1, pages.length || 1);
        const target = Math.max(0, Math.min(to, totalPages - 1));

        setPage(target);

        const w = Math.max(1, el.clientWidth);
        const x = target * w;
        const behavior = instantNav ? "auto" : "smooth";

        try {
            (el as any).scrollTo({ left: x, behavior });
        } catch {
            el.scrollLeft = x;
        }

        // ✅ 버튼 이동 시 pageMeta 즉시 갱신 (scroll 이벤트 누락 대비)
        onMetaRef.current?.(target, totalPages);

        // ✅ 스크롤 반영이 늦는 WebView 보정
        requestAnimationFrame(() => {
            const w2 = Math.max(1, el.clientWidth);
            const idx = Math.floor((el.scrollLeft + w2 * 0.5) / w2);
            onMetaRef.current?.(idx, totalPages);
        });
    };

    useImperativeHandle(
        ref,
        () => ({
            goTo: (n: number) => go(n),
            getTotal: () => pages.length,
        }),
        [pages.length]
    );

    return (
        <div ref={wrapRef} style={{ height, overflow: "hidden", position: "relative" }}>
            <div
                ref={listRef}
                style={{
                    height: "100%",
                    width: "100%",
                    display: "grid",
                    gridAutoFlow: "column",
                    gridAutoColumns: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none" as any,
                    msOverflowStyle: "none" as any,
                    scrollSnapType: "x mandatory",
                }}
            >
                {pages.map((p, i) => (
                    <div
                        key={i}
                        style={{
                            scrollSnapAlign: "start",
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            overflow: "hidden",
                        }}
                    >
                        {p.type === "image" && (
                            <div
                                style={{
                                    width: "calc(100% - 4px)",
                                    height: "100%",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#f4f4f4",
                                    borderRadius: s(10),
                                    overflow: "hidden",
                                    boxSizing: "border-box",
                                    flexShrink: 0,
                                    margin: "0 2px",
                                }}
                            >
                                <img
                                    src={p.content}
                                    alt="콘텐츠 이미지"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                    }}
                                />
                            </div>
                        )}

                        {p.type === "text" && (
                            <div
                                style={{
                                    width: "calc(100% - 4px)",
                                    height: "100%",
                                    margin: "0 2px",
                                    padding: `${padY}px ${padX}px`,
                                    boxSizing: "border-box",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {/* 본문 */}
                                <div
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: "hidden",
                                        fontSize: fontPx,
                                        lineHeight: `${linePx}px`,
                                        whiteSpace: p.isHtml ? "normal" : "pre-wrap",
                                        wordBreak: "break-word",
                                        color: COLOR.text,
                                    }}
                                >
                                    {p.isHtml
                                        ? <div className="tox-content-html" style={{ width: "100%" }} dangerouslySetInnerHTML={{ __html: p.content }} />
                                        : <div>{(p.content || "내용이 없습니다.").replace(/\n{2,}/g, '\n')}</div>
                                    }
                                </div>

                                {/* 공감 — 마지막 페이지에만 */}
                                {p.showReaction && (
                                    <div
                                        style={{
                                            flexShrink: 0,
                                            borderRadius: s(RADIUS.xl),
                                            border: BORDER.light,
                                            background: COLOR.white,
                                            boxSizing: "border-box",
                                            padding: `${px(32)} ${px(24)}`,
                                            marginTop: px(16),
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: sf(36),
                                                fontWeight: FW.extraBold,
                                                marginBottom: px(24),
                                                textAlign: "center",
                                            }}
                                        >
                                            표정으로 공감하기
                                        </div>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                                                columnGap: px(8),
                                                width: "100%",
                                                alignItems: "center",
                                            }}
                                        >
                                            {REACTIONS.map((r) => {
                                                const count = reactions[r.code as keyof ReactionCounts] ?? 0;
                                                return (
                                                    <button
                                                        key={r.key}
                                                        type="button"
                                                        onClick={() => onReact(r.key)}
                                                        style={{
                                                            border: "none",
                                                            background: "transparent",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            gap: px(8),
                                                            padding: 0,
                                                            outline: "none",
                                                        }}
                                                    >
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                                                            <img
                                                                src={ICONS[r.code as keyof typeof ICONS]}
                                                                alt={r.label}
                                                                style={{ height: px(100), width: "auto", display: "block", objectFit: "contain" }}
                                                            />
                                                        </div>
                                                        <span style={{ fontSize: sf(28), fontWeight: FW.bold, lineHeight: 1.3 }}>
                                                            {count}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                ))}
            </div>

            {/* ✅ 페이지 분할 계산 중 오버레이 (가벼운 로딩 표시) */}
            {splitting && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255,255,255,0.55)",
                        pointerEvents: "none",
                    }}
                >
                    <div
                        style={{
                            background: COLOR.white,
                            border: BORDER.light,
                            borderRadius: s(RADIUS.lg),
                            padding: `${px(16)} ${px(20)}`,
                            fontSize: sf(26),
                            fontWeight: FW.bold,
                            color: COLOR.text,
                        }}
                    >
                        로딩 중…
                    </div>
                </div>
            )}
        </div>
    );
});


export default function TalkToxDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { state } = useLocation() as { state?: LocationState };
    const { s, px, sf, sfpx, widthPx, viewportHeight } = usePageScale({ baseW: BASE.W, max: 1 });

    const [item, setItem] = useState<WeeklyTox | null>(null);
    const pagerRef = useRef<ContentPagerHandle>(null);
    const [pageMeta, setPageMeta] = useState({ page: 1, total: 1 });
    const handleMeta = useCallback((idx: number, total: number) => { setPageMeta({ page: idx + 1, total }); }, []);

    // 콘텐츠 영역 높이 (동적 계산용)
    const containerRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState(400);

    // 반응형 콘텐츠 높이 계산
    // 헤더(HEADER.H) + 상단 여백 + 제목/작성자 영역 + 페이저 네비 + 하단 여백
    const HEADER_TITLE_HEIGHT = s(120); // 제목+작성자 영역 대략 높이
    const usedHeight = HEADER.H + s(BASE.CONTENT_TOP_GAP) + HEADER_TITLE_HEIGHT + s(BASE.PAGER_NAV_H) + s(44);
    const calculatedContentHeight = viewportHeight > 0 ? Math.max(200, viewportHeight - usedHeight) : 400;

    // 전체 카운트
    const [reactions, setReactions] = useState<ReactionCounts>({
        like: 0,
        funny: 0,
        wow: 0,
        angry: 0,
        sad: 0,
        total: 0,
    });

    // 현재 내가 누른 리액션 (없으면 null)
    const [userReaction, setUserReaction] = useState<ToxReactionType | null>(null);

    /* ───────────── 콘텐츠 높이 계산 ───────────── */
    useLayoutEffect(() => {
        const updateHeight = () => {
            if (containerRef.current) {
                const h = containerRef.current.clientHeight;
                setContentHeight(h);
            }
        };
        updateHeight();

        const RO: any = (window as any).ResizeObserver;
        let ro: any = null;
        if (RO && containerRef.current) {
            ro = new RO(updateHeight);
            ro.observe(containerRef.current);
        } else {
            window.addEventListener("resize", updateHeight);
            return () => window.removeEventListener("resize", updateHeight);
        }
        return () => { if (ro) ro.disconnect(); };
    }, [item]);

    /* ───────────── 데이터 로딩 ───────────── */
    useEffect(() => {
        if (!id) return;
        const numId = Number(id);
        if (isNaN(numId) || numId <= 0) return;

        api(`/tox/${numId}/view`, { method: "POST" }).catch(() => {});
        (async () => {
            try {
                const data = await weeklyToxGet(numId);
                const tox: any = data.tox || data;
                setItem(tox);

                const like = tox.reaction_like || 0;
                const funny = tox.reaction_funny || 0;
                const wow = tox.reaction_wow || 0;
                const angry = tox.reaction_angry || 0;
                const sad = tox.reaction_sad || 0;

                setReactions({
                    like,
                    funny,
                    wow,
                    angry,
                    sad,
                    total: like + funny + wow + angry + sad,
                });

                const my =
                    (tox.my_reaction as ToxReactionType | undefined) ??
                    (tox.myReaction as ToxReactionType | undefined) ??
                    null;
                if (my) setUserReaction(my);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [id]);

    /* ───────────── 뒤로가기 ───────────── */
    const handleBack = () => {
        if (state?.fromPage || state?.fromTab) {
            nav(-1);
        } else {
            nav("/tox");
        }
    };

    /* ───────────── 리액션 클릭 ───────────── */
    const handleReact = async (t: ToxReactionType) => {
        if (!item?.id) return;

        if (!isLoggedIn()) {
            const ok = await customConfirm("로그인을 해야 공감하실 수 있습니다.", "로그인페이지로 이동");
            if (ok) nav("/auth/login");
            return;
        }

        const nextType: ToxReactionType | null = userReaction === t ? null : t;

        try {
            const res: any = await weeklyToxReact(item.id, nextType);

            if (res?.reactions) {
                const {
                    like = 0,
                    funny = 0,
                    wow = 0,
                    angry = 0,
                    sad = 0,
                    total,
                } = res.reactions;

                setReactions({
                    like,
                    funny,
                    wow,
                    angry,
                    sad,
                    total:
                        typeof total === "number"
                            ? total
                            : like + funny + wow + angry + sad,
                });
            } else {
                setReactions((prev) => {
                    let { like, funny, wow, angry, sad } = prev;

                    if (userReaction) {
                        if (userReaction === "LIKE" && like > 0) like -= 1;
                        if (userReaction === "FUNNY" && funny > 0) funny -= 1;
                        if (userReaction === "WOW" && wow > 0) wow -= 1;
                        if (userReaction === "ANGRY" && angry > 0) angry -= 1;
                        if (userReaction === "SAD" && sad > 0) sad -= 1;
                    }

                    if (nextType) {
                        if (nextType === "LIKE") like += 1;
                        if (nextType === "FUNNY") funny += 1;
                        if (nextType === "WOW") wow += 1;
                        if (nextType === "ANGRY") angry += 1;
                        if (nextType === "SAD") sad += 1;
                    }

                    const total = like + funny + wow + angry + sad;
                    return { like, funny, wow, angry, sad, total };
                });
            }

            if (res?.userReaction !== undefined) {
                setUserReaction(res.userReaction || null);
            } else {
                setUserReaction(nextType);
            }
        } catch (e) {
            console.error(e);
            notify("공감 등록에 실패했어요.");
        }
    };

    const imageUrl = resolveImageUrl((item as any)?.body_image_url || null);

    // 페이지 네비게이션 핸들러
    const goFirst = () => pagerRef.current?.goTo(0);
    const goPrev = () => pagerRef.current?.goTo(pageMeta.page - 2);
    const goNext = () => pagerRef.current?.goTo(pageMeta.page);
    const goLast = () => pagerRef.current?.goTo(pageMeta.total - 1);

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({ next: goNext, prev: goPrev, first: goFirst, last: goLast });
        return () => unregisterPager();
    }, [pageMeta.page, pageMeta.total]);

    return (
        <PageShell
            title="INNO STORY"
            onBack={handleBack}
            backIcon={ICONS.back}
            width={widthPx}
        >
            <div
                style={{
                    height: `calc(var(--app-lvh, 100vh) - ${HEADER.H}px)`,
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "hidden",
                }}
            >
                {item && (
                    <div
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            paddingLeft: px(BASE.MARGIN_X),
                            paddingRight: px(BASE.MARGIN_X),
                            paddingTop: px(BASE.CONTENT_TOP_GAP),
                            paddingBottom: px(20),
                            overflow: "hidden",
                        }}
                    >
                        {/* 헤더 영역 (제목 + 작성자) - 고정 */}
                        <div style={{ flexShrink: 0, marginBottom: px(24) }}>
                            <div
                                style={{
                                    fontSize: sf(36),
                                    fontWeight: FW.extraBold,
                                    lineHeight: 1.3,
                                    color: COLOR.text,
                                    marginBottom: px(8),
                                }}
                            >
                                {item.title}
                            </div>
                            <div
                                style={{
                                    fontSize: sf(30),
                                    fontWeight: 400,
                                    color: "#333",
                                }}
                            >
                                작성자 : {item.author?.name ?? "익명"}
                            </div>
                        </div>

                        {/* 콘텐츠 페이저 영역 (이미지 + 본문 + 공감하기) */}
                        <div
                            ref={containerRef}
                            style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
                        >
                            <ContentPager
                                ref={pagerRef}
                                imageUrl={imageUrl}
                                text={((item as any).content_html || item.content || "").trim()}
                                height={contentHeight > 0 ? contentHeight - s(BASE.PAGER_NAV_H) : calculatedContentHeight}
                                fontPx={s(32)}
                                linePx={s(52)}
                                padY={s(10)}
                                padX={0}
                                onMeta={handleMeta}
                                s={s}
                                px={px}
                                sf={sf}
                                reactions={reactions}
                                userReaction={userReaction}
                                onReact={handleReact}
                                instantNav={true}
                                previewChars={700}
                            />

                            {/* 페이지 네비게이션 - 목록 페이지 스타일 */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: px(BASE.PAGER_NAV_H),
                                    flexShrink: 0,
                                }}
                            >
                                {/* 맨 앞 */}
                                <button
                                    onClick={goFirst}
                                    aria-label="맨 앞"
                                    disabled={pageMeta.page <= 1}
                                    style={{
                                        lineHeight: 0,
                                        background: "transparent",
                                        marginRight: px(48),
                                        border: "none",
                                        opacity: pageMeta.page > 1 ? 1 : 0.4,
                                        cursor: pageMeta.page > 1 ? "pointer" : "default",
                                    }}
                                >
                                    <img src={ICONS.first} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                                </button>

                                {/* 한칸 앞 */}
                                <button
                                    onClick={goPrev}
                                    aria-label="이전"
                                    disabled={pageMeta.page <= 1}
                                    style={{
                                        lineHeight: 0,
                                        background: "transparent",
                                        marginRight: px(46),
                                        border: "none",
                                        opacity: pageMeta.page > 1 ? 1 : 0.4,
                                        cursor: pageMeta.page > 1 ? "pointer" : "default",
                                    }}
                                >
                                    <img src={ICONS.prev} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                                </button>

                                {/* 페이지 표시 */}
                                <span
                                    style={{
                                        fontWeight: FW.bold,
                                        fontFeatureSettings: "'tnum' 1",
                                        fontSize: sfpx(34),
                                        minWidth: px(120),
                                        textAlign: "center",
                                        marginRight: px(46),
                                    }}
                                >
                                    {pageMeta.page} / {pageMeta.total}
                                </span>

                                {/* 한칸 뒤 */}
                                <button
                                    onClick={goNext}
                                    aria-label="다음"
                                    disabled={pageMeta.page >= pageMeta.total}
                                    style={{
                                        lineHeight: 0,
                                        background: "transparent",
                                        marginRight: px(48),
                                        border: "none",
                                        opacity: pageMeta.page < pageMeta.total ? 1 : 0.4,
                                        cursor: pageMeta.page < pageMeta.total ? "pointer" : "default",
                                    }}
                                >
                                    <img src={ICONS.next} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                                </button>

                                {/* 맨 뒤 */}
                                <button
                                    onClick={goLast}
                                    aria-label="맨 뒤"
                                    disabled={pageMeta.page >= pageMeta.total}
                                    style={{
                                        lineHeight: 0,
                                        background: "transparent",
                                        border: "none",
                                        opacity: pageMeta.page < pageMeta.total ? 1 : 0.4,
                                        cursor: pageMeta.page < pageMeta.total ? "pointer" : "default",
                                    }}
                                >
                                    <img src={ICONS.last} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
}
